/**
 * @fileoverview Patient table component with CRUD actions
 * Uses DmsEntityTable for consistent behavior across the application
 */
import React, { useRef, useState, useCallback, useMemo } from "react";
import { intl, fetchData, HTTP_STATUS } from "dmsbasereact";
import { formatCpf } from "../../utils/formatters";
import { PatientStatusEnum, PatientStatus } from "../../constants/PatientStatusEnum";
import { ModalFormCreatePatient } from "../ModalFormCreatePatient/ModalFormCreatePatient";
import { Typography, Chip } from "@mui/material";
import { PatientDetailModal } from "./PatientDetailModal";
import { ModalPatientReasonStatus } from "../ModalPatientReasonStatus/ModalPatientReasonStatus";
import { calculateAge } from "../../utils/calculateAge";
import { DmsEntityTable } from "../DmsEntityTable";
import Swal from "sweetalert2";

/**
 * Patient table component using DmsEntityTable
 */
export const PatientTable = () => {
  const table = useRef(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showJustificationModal, setShowJustificationModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientForStatusChange, setPatientForStatusChange] = useState(null);

  // Handlers
  const handleViewDetails = useCallback((row) => {
    setSelectedPatient(row);
    setShowDetailModal(true);
  }, []);

  const handleCloseDetailModal = useCallback(() => {
    setShowDetailModal(false);
    setSelectedPatient(null);
  }, []);

  const handleToggleStatus = useCallback((row) => {
    const data = { ...row };
    data.status = data.status === PatientStatusEnum.ACTIVE
      ? PatientStatusEnum.INACTIVE
      : PatientStatusEnum.ACTIVE;
    setPatientForStatusChange(data);
    setShowJustificationModal(true);
  }, []);

  const handleDelete = useCallback(async (row) => {
    const result = await Swal.fire({
      title: intl.get("DeletePatient", { name: row.name }).d(`Delete Patient ${row.name}`),
      html: `
        <p>${intl.get("DeletePatientConfirmation").d("Are you sure you want to delete this patient?")}</p>
        <p><strong>${row.name}</strong></p>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: intl.get("Delete").d("Delete"),
      cancelButtonText: intl.get("Cancel").d("Cancel"),
    });

    if (!result.isConfirmed) return;

    const response = await fetchData(`/patient/${row.pk}`, "DELETE", true, {}, false, true);
    if (response.status === HTTP_STATUS.OK_200) {
      table.current?.updateTable();
    }
  }, []);

  const handleSavePatient = useCallback(async (patientPk, data) => {
    try {
      const response = await fetchData(`/patient/${patientPk}`, "PUT", true, data, false, true);
      if (response.status === HTTP_STATUS.OK_200) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      return null;
    }
  }, []);

  const handleOpenCreateModal = useCallback(() => {
    setShowCreateModal(true);
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    setShowCreateModal(false);
  }, []);

  // Status chip renderer
  const getStatusChip = useCallback((row) => (
    <Chip
      label={PatientStatus.getText(row.status)}
      color={PatientStatus.getColor(row.status)}
      size="small"
    />
  ), []);

  // Contact authorization helper functions
  const getContactAuthorizationText = (value) => {
    if (value === true) return intl.get("Yes").d("Sim");
    if (value === false) return intl.get("No").d("Não");
    return intl.get("NotProvided").d("Não Informado");
  };

  const getContactAuthorizationColor = (value) => {
    if (value === true) return "success";
    if (value === false) return "error";
    return "default";
  };

  // Contact authorization chip renderer
  const getContactAuthorizationChip = useCallback((row) => (
    <Chip
      label={getContactAuthorizationText(row.contact_authorization)}
      color={getContactAuthorizationColor(row.contact_authorization)}
      size="small"
    />
  ), []);

  // Column definitions
  const columns = useMemo(() => [
    {
      label: intl.get("Number").d("Number"),
      column_name: "patient_identifier",
      filter: true,
    },
    {
      label: intl.get("Name").d("Name"),
      column_name: "name",
      filter: true,
      sortable: true,
    },
    {
      label: intl.get("CPF").d("CPF"),
      column_name: "cpf",
      filter: true,
      sortable: true,
      function: (value) => formatCpf(value) || "-",
    },
    {
      label: intl.get("Age").d("Age"),
      column_name: "birth_date",
      sortable: true,
      function: (value) => <Typography>{calculateAge(value)}</Typography>,
    },
    {
      label: intl.get("Status").d("Status"),
      column_name: "status",
      width: "100px",
      element: getStatusChip,
    },
    {
      label: intl.get("ContactAuthorization").d("Autoriza Contato"),
      column_name: "contact_authorization",
      width: "150px",
      element: getContactAuthorizationChip,
    },
  ], [getStatusChip, getContactAuthorizationChip]);

  // Check if patient is active
  const isPatientActive = useCallback((row) => row.status === PatientStatusEnum.ACTIVE, []);

  return (
    <>
      <DmsEntityTable
        ref={table}
        url="patient/table"
        columns={columns}
        onView={handleViewDetails}
        onDelete={handleDelete}
        onToggleStatus={handleToggleStatus}
        onCreate={handleOpenCreateModal}
        isActiveCheck={isPatientActive}
        createButtonLabel={intl.get("CreatePatient").d("Create Patient")}
        deleteTitle={intl.get("DeletePatient", { name: "" }).d("Delete Patient")}
        activateTitle={intl.get("ActivatePatient").d("Activate Patient")}
        deactivateTitle={intl.get("DeactivatePatient").d("Deactivate Patient")}
      />

      <ModalFormCreatePatient
        isVisible={showCreateModal}
        onClose={handleCloseCreateModal}
        table={table}
      />

      <PatientDetailModal
        open={showDetailModal}
        onClose={handleCloseDetailModal}
        patient={selectedPatient}
        onSave={handleSavePatient}
        table={table}
      />

      {showJustificationModal && patientForStatusChange && (
        <ModalPatientReasonStatus
          isVisible={showJustificationModal}
          onClose={() => {
            setShowJustificationModal(false);
            setPatientForStatusChange(null);
            table.current?.updateTable();
          }}
          patientData={patientForStatusChange}
          table={table}
        />
      )}
    </>
  );
};
